import React, { useState } from 'react';
import '../../styles/Page.css';
import '../../styles/Hero.css';


import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../contexts/AuthContext';
import useLocalStorage from '../../hooks/useLocalStorage';
import PostEnhancerHero from './PostEnhancerHero';
import HowTo from './HowTo';
import ResultsList from './ResultsList';
import LegalNote from '../../components/LegalNote';

function PostEnhancer() {
  const [subreddit, setSubreddit] = useLocalStorage('postEnhancer_subreddit', '');
  const [username, setUsername] = useState('');
  const [postTitle, setPostTitle] = useState('');
  const [postContent, setPostContent] = useState('');
  const { loading, error, request: enhanceApi, clearError } = useApi();
  const { fetchQuota } = useAuth();
  const [enhancementResult, setEnhancementResult] = useState(null);


  const handleFocus = (event) => event.target.select();

  const getSubredditName = (input) => {
    const trimmedInput = input.trim();
    const match = trimmedInput.match(/reddit\.com\/r\/([^/]+)/);
    if (match) return match[1];
    if (trimmedInput.startsWith('r/')) return trimmedInput.substring(2);
    return trimmedInput;
  };

  const getUsername = (input) => {
    const trimmedInput = input.trim();
    const urlMatch = trimmedInput.match(/reddit\.com\/(?:user|u)\/([^/]+)/);
    if (urlMatch) return urlMatch[1];
    if (trimmedInput.startsWith('u/')) return trimmedInput.substring(2);
    return trimmedInput;
  };

  const handleEnhance = async (e) => {
    e.preventDefault();
    setEnhancementResult(null);

    const subredditName = getSubredditName(subreddit);
    const sanitizedUsername = getUsername(username);

    try {
      const data = await enhanceApi('/api/enhance_post', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subreddit: subredditName,
          username: sanitizedUsername,
          post_title: postTitle,
          post_content: postContent
        })
      });
      setEnhancementResult(data);
      fetchQuota();
    } catch (err) {
      console.error("Enhancement failed:", err);
    }
  };

  const handleClear = () => {
    setSubreddit('');
    setUsername('');
    setPostTitle('');
    setPostContent('');
    clearError();
    setEnhancementResult(null);
  };

  const handleSaveToJson = () => {
    if (!enhancementResult) return;

    const jsonString = `data:text/json;charset=utf-8,${encodeURIComponent(
      JSON.stringify(enhancementResult, null, 2)
    )}`;
    const link = document.createElement("a");
    link.href = jsonString;
    link.download = `enhancement_result_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.json`;

    link.click();
  };

  return (
    <>
      <title>Post Enhancer - WizeHelper</title>
      <meta name="description" content="Adapt your writing to the style of any subreddit. Get suggestions to improve your post's title and content to maximize engagement." />


      <main className="mx-auto px-6 py-8" style={{ maxWidth: '88rem' }}>
        <PostEnhancerHero
          subreddit={subreddit}
          setSubreddit={setSubreddit}
          username={username}
          setUsername={setUsername}
          postTitle={postTitle}
          setPostTitle={setPostTitle}
          postContent={postContent}
          setPostContent={setPostContent}
          handleEnhance={handleEnhance}
          loading={loading}
          handleFocus={handleFocus}
        />
        <LegalNote />
        {error && (
          <div className="p-4 mb-4 text-sm text-red-700 bg-red-100 rounded-lg" role="alert" data-testid="postEnhancer-error">
            {error.status === 403 || error.status === 429 ? (
              error.message
            ) : (
              <><span className="font-medium">Error!</span> {error.message}</>
            )}
          </div>
        )}
        {loading ? (
          <div className="text-center my-12" data-testid="postEnhancer-loading">
            <div className="spinner"></div>
          </div>
        ) : (
          <>
            {enhancementResult ? (
              <ResultsList
                enhancementResult={enhancementResult}
                handleClear={handleClear}
                handleSaveToJson={handleSaveToJson}
              />
            ) : (
              <div className="my-12" data-testid="postEnhancer-howToContainer">
                <HowTo />
              </div>
            )}
          </>
        )}
      </main>
    </>
  );
}

export default PostEnhancer;